import Styled from 'styled-components'
import { ModulePerms } from '../../../access-control/domain/module'
import { AppLayoutSider, AppMenu, AppMenuItem } from '@/shared/ui'
import { useTranslation } from 'react-i18next'
import { Can } from '../../../access-control/infrastructure/roles-permissions/access-control'
import { useGateValue } from '@statsig/react-bindings'
import { SideMenuModule } from '../../domain/entities'
import {
  BookOpenIcon,
  ListMagnifyingGlassIcon,
  StorefrontIcon,
  HeadsetIcon,
  FolderSimpleUserIcon,
  ChartLineUpIcon,
  FlagIcon,
  ListBulletsIcon,
  UsersThreeIcon,
  PathIcon,
  GearSixIcon,
  ShoppingCartSimpleIcon,
  EnvelopeIcon,
  CurrencyCircleDollarIcon,
  CodesandboxLogoIcon,
  ClockClockwiseIcon,
  StarIcon,
  ChartBarIcon,
  FileIcon,
  TrayIcon,
  EnvelopeSimpleIcon,
  ChatIcon,
  ClipboardTextIcon
} from '@phosphor-icons/react'
import { useAuth } from '../../../../../auth/Auth.hook'
import { siderStyle } from './SideMenu.styles'

const Logo = Styled.figure`
  text-align: center;
  padding: 5px;
  margin: 0;
  & > img {
    width: 55px;
    height: 55px;
    object-fit: contain;
  }
`

const MenuContainer = Styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const TopMenuSection = Styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  /* Personalizar scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`

const BottomMenuSection = Styled.div`
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 8px;
`

const HAS_ROUTES = 'hasRoutes'
const HAS_ACCOUNTING = 'hasAccounting'

type SideMenuProps = {
  history: any
  view: string
  featureFlags: any
  isMobile: boolean
  onClick?: () => void
}
const SideMenu = ({ history, view, featureFlags, isMobile, onClick }: SideMenuProps) => {
  const { t } = useTranslation()
  const isComandaMenuActive = useGateValue('comanda_menu')
  const isSageMenuActive = useGateValue('is_sage_menu_active')
  const isEcommerceMenuActive = useGateValue('is_ecommerce_menu_active')
  const isWmsMenuActive = useGateValue('is_wms_menu_active')
  const isMetabaseActive = useGateValue('is_metabase_active')
  const isStarStreamActive = useGateValue('is_star_stream_active')
  const { userInfo } = useAuth()

  const topModuleMenu: SideMenuModule[] = [
    {
      key: 'orders',
      path: 'orders',
      icon: <ListBulletsIcon />,
      label: 'menu.jobs-search',
      isAvailable: Can([ModulePerms.JOB])
    },
    {
      key: 'tasks',
      path: 'tasks',
      icon: <TrayIcon />,
      label: 'menu.tasks',
      isAvailable: Can([ModulePerms.TAS])
    },
    {
      key: 'alert-management',
      path: 'alert-management/orders',
      icon: <FlagIcon />,
      label: 'menu.jobs-list',
      isAvailable: Can([ModulePerms.FLA])
    },
    {
      key: 'routes',
      path: 'routes',
      icon: <PathIcon />,
      label: 'menu.routes',
      isAvailable: Can([ModulePerms.ROU]) && featureFlags[HAS_ROUTES] === 'true'
    },
    {
      key: 'metrics',
      path: 'metrics/order-status',
      icon: <ChartLineUpIcon />,
      label: 'metrics',
      isAvailable: Can([ModulePerms.MET])
    },
    {
      key: 'analytics',
      path: 'analytics',
      icon: <ChartBarIcon />,
      label: 'metabase.analytics',
      isAvailable: Can([ModulePerms.ANA]) && isMetabaseActive
    },
    {
      key: 'users',
      path: 'user-management/users',
      icon: <UsersThreeIcon />,
      label: 'menu.users',
      isAvailable: Can([ModulePerms.USE])
    },
    {
      key: 'resources',
      path: 'resources/stores',
      icon: <FolderSimpleUserIcon />,
      label: 'menu.resources',
      isAvailable: Can([ModulePerms.RES])
    },
    {
      key: 'system-parameters',
      path: 'system-parameters/general',
      icon: <GearSixIcon />,
      label: 'systemParameters',
      isAvailable: Can([ModulePerms.PAR])
    },
    {
      key: 'accounting',
      path: 'accounting',
      icon: <CurrencyCircleDollarIcon />,
      label: 'Accounting',
      isAvailable: Can([ModulePerms.ACC]) && featureFlags[HAS_ACCOUNTING] === 'true'
    },
    {
      key: 'ecommerceAdmin',
      path: 'ecommerceAdmin',
      icon: <ShoppingCartSimpleIcon />,
      label: 'E-commerce Admin',
      isAvailable: Can([ModulePerms.ECO]) && isEcommerceMenuActive
    },
    {
      key: 'inbox',
      path: 'inbox',
      icon: <ChatIcon />,
      label: 'menu.inbox',
      isAvailable: Can([ModulePerms.INB])
    },
    {
      key: 'wms',
      path: 'wms',
      icon: <CodesandboxLogoIcon />,
      label: 'WMS',
      isAvailable: Can([ModulePerms.WMS]) && isWmsMenuActive
    },
    {
      key: 'segmenter',
      path: 'segmenter',
      icon: <EnvelopeSimpleIcon />,
      label: 'Segmenter',
      isAvailable: Can([ModulePerms.SEG])
    },
    {
      key: 'comanda',
      path: 'comanda',
      icon: <ClipboardTextIcon />,
      label: 'Comanda',
      isAvailable: Can([ModulePerms.COM]) && isComandaMenuActive
    },
    {
      key: 'order-ratings',
      path: 'order-ratings',
      icon: <StarIcon />,
      label: 'order-ratings',
      isAvailable: Can([ModulePerms.ORA]) && isSageMenuActive
    },
    {
      key: 'jonah-jameson',
      path: 'jonah-jameson',
      icon: <EnvelopeIcon />,
      label: 'Jonah jameson',
      isAvailable: Can([ModulePerms.JJS])
    },
    {
      key: 'activity-log',
      path: 'activity-log',
      icon: <ClockClockwiseIcon />,
      label: 'activityLog.activityLog',
      isAvailable: Can([ModulePerms.LOG])
    },
    {
      key: 'reports',
      path: 'reports',
      icon: <FileIcon />,
      label: 'reports.title',
      isAvailable: Can([ModulePerms.SST]) && isStarStreamActive
    },
    {
      key: 'shopperWeb',
      path: 'shopperWeb',
      icon: <ListMagnifyingGlassIcon />,
      label: 'picking-web',
      isAvailable: Can([ModulePerms.PIC])
    },
    {
      key: 'marketplace-manager',
      path: 'marketplace-manager',
      icon: <StorefrontIcon />,
      label: 'marketPlaceManager',
      isAvailable: Can([ModulePerms.MAR])
    },
    {
      key: 'call-center',
      path: 'call-center',
      icon: <HeadsetIcon />,
      label: 'roles-permissions.roles.call_center',
      isAvailable: Can([ModulePerms.CAL])
    }
  ]

  const bottomModuleMenu: SideMenuModule[] = [
    {
      key: 'leapdocs',
      path: 'leapdocs',
      icon: <BookOpenIcon />,
      label: 'leapdocs',
      isAvailable: true
    }
  ]

  const topModuleKeys = topModuleMenu.map((module) => module.key)
  const bottomModuleKeys = bottomModuleMenu.map((module) => module.key)
  const isTopModuleActive = topModuleKeys.includes(view)
  const isBottomModuleActive = bottomModuleKeys.includes(view)

  const changeView = (viewToGo: string) => {
    onClick && onClick()
    if (viewToGo === 'shopperWeb') {
      window.open(import.meta.env.VITE_MFE_SHOPPER_WEB, '_blank')
    } else {
      history.push(`/${viewToGo}`)
    }
  }

  return userInfo ? (
    <AppLayoutSider style={siderStyle} trigger={null} collapsible collapsed={!isMobile}>
      <MenuContainer>
        <Logo onClick={() => onClick && onClick()}>
          <img src={userInfo?.client?.logoUrl} alt='client-logo' />
        </Logo>

        <TopMenuSection>
          <AppMenu theme='dark' mode='inline' selectedKeys={isTopModuleActive ? [view] : []}>
            {topModuleMenu.map((module: SideMenuModule) => {
              if (module.isAvailable) {
                return (
                  <AppMenuItem
                    key={module.key}
                    onClick={() => {
                      changeView(module.path)
                    }}
                    icon={module.icon}
                  >
                    <span>{t(module.label)}</span>
                  </AppMenuItem>
                )
              }
              return null
            })}
          </AppMenu>
        </TopMenuSection>

        <BottomMenuSection>
          <AppMenu theme='dark' mode='inline' selectedKeys={isBottomModuleActive ? [view] : []}>
            {bottomModuleMenu.map((module: SideMenuModule) => {
              if (module.isAvailable) {
                return (
                  <AppMenuItem
                    key={module.key}
                    onClick={() => {
                      changeView(module.path)
                    }}
                    icon={module.icon}
                  >
                    <span>{t(module.label)}</span>
                  </AppMenuItem>
                )
              }
              return null
            })}
          </AppMenu>
        </BottomMenuSection>
      </MenuContainer>
    </AppLayoutSider>
  ) : null
}

export { SideMenu }
